import { parseDataTransform, uploadFile } from '@acme/common'
import Dialog from 'global/guides/Dialog'
import { useAuth } from 'hooks'
import { useAusenciasOutletContext } from 'pages/Ausencias'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import {
    afastamento,
    AfastamentoV2,
    afastamentoV2,
    hasPermissionToAnyAfastamento,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import FormNovoAfastamento from 'templates/ManutencaoDoPonto/Digitacao/forms/NovoAfastamento'
import AvisoAfastamento from 'templates/ManutencaoDoPonto/Digitacao/modals/AvisoAfastamento'
import { z } from 'zod'
import LancamentosAnteriores from '../lancamentos-anteriores'
import invalidateAusenciasQuery from './invalidateAusenciasQuery'

const baseUrl = import.meta.env.VITE_BASE_URL

export default function AddAfastamentoModal() {
    const {
        decoded: { categoria },
    } = useAuth()
    const { mutateAsync, isLoading } = afastamentoV2.mutations.useCreate({
        invalidateQuery: true,
    })
    const empresaId = useStore(state => state.companyId)
    const [funcionarios, setIds] = React.useState<Array<number>>([])
    const [values, setValues] = useState<Required<AfastamentoV2>>()
    const [isFetching, setIsFetching] = useState(false)

    const { data } = useAusenciasOutletContext()
    const rows =
        funcionarios.length > 1
            ? []
            : data
                  ?.filter(({ tipoAusencia }) => tipoAusencia === 'Afastamento')
                  ?.filter(item => item.funcionarioId == funcionarios.at(0))

    const handleSubmit: SubmitHandler<Required<AfastamentoV2>> = async ({
        fileUrl,
        justificativa,
        ...data
    }) => {
        const filename = await uploadFile(fileUrl, baseUrl)
        return mutateAsync(
            {
                ...parseDataTransform(data),
                justificativa: justificativa,
                fileUrl: fileUrl ? filename : null,
                empresaId,
                funcionarios,
                origem: [3, 4, 6].includes(categoria) ? 1 : 2,
            },
            {
                onSuccess: async () => {
                    await invalidateAusenciasQuery()
                    toast.success('Afastamento adicionado com sucesso!')
                },
                onError: err => {
                    toast.error(err.message)
                },
            },
        )
    }

    return (
        <Dialog.Root
            loading={isLoading || isFetching}
            onSubmit={async values => {
                setIsFetching(true)
                if (
                    await afastamento
                        .get('existe-registro', {
                            dataInicial: values.dataInicial,
                            dataFinal: values.dataFinal,
                            funcionarios,
                            empresaId,
                            ocorrenciaId: values.ocorrenciaId,
                        })
                        .catch(() => false)
                        .finally(() => setIsFetching(false))
                )
                    return setValues(values)
                return handleSubmit(values)
            }}
            height="auto"
            schema={z.object({
                ocorrenciaId: z.number().positive(),
                dataInicial: z.string().min(1),
                dataFinal: z.string().min(1),
                fileUrl: z.any().nullish(),
                justificativa: z.string().max(190).default(''),
            })}
            open={hasPermissionToAnyAfastamento('create')}
        >
            <Dialog.Title>Novo afastamento</Dialog.Title>
            <Dialog.Content dividers>
                <FormNovoAfastamento setIds={setIds} />
                <LancamentosAnteriores rows={rows} />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit />
            </Dialog.Actions>
            <AvisoAfastamento
                open={!!values}
                onClose={() => setValues(undefined)}
                onSubmit={() => {
                    handleSubmit(values!)
                    setValues(undefined)
                }}
            />
        </Dialog.Root>
    )
}
